/**
 * ReviseProposalMode (Phase 23.2.5 — Living SOPs)
 *
 * Inline expandable panel that appears below the proposal when a trainer
 * clicks "Ask AI to revise". This is NOT a centered modal — it's an in-flow
 * component that slides down into the proposal detail layout, keeping the
 * proposal content visible above as reference while the trainer types.
 *
 * Design lock §15 Q7 (Apr 20): the ONLY way to modify proposal content is
 * natural-language feedback to Claude. Trainers cannot directly edit text.
 * This preserves the clean AI-authored signal for the auto-approval
 * classifier we plan to train in Phase 24+ from trainer decisions.
 *
 * UX states:
 *   - idle        → textarea + "Ask AI to revise" button (primary indigo)
 *   - submitting  → "AI is revising..." spinner, textarea + Cancel disabled
 *   - unchanged   → info banner: "AI didn't change anything — try more
 *                   specific feedback." Panel stays open for refinement.
 *   - success     → panel closes; parent refetches the proposal detail query
 *   - error       → error banner; panel stays open for retry
 *
 * Accessibility:
 *   - role="region" + aria-label="Revise proposal"
 *   - Live region on status updates (aria-live="polite")
 *   - Textarea has an associated <label>
 *   - Character counter announces on keystroke (aria-live handles it
 *     naturally on each render via the count span)
 *   - Escape closes when not submitting (handled by parent keyboard handler
 *     gating — see ProposalDetailPage reviseOpen guard)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Info, Loader2, MessageSquarePlus, X } from 'lucide-react';
import { useReviseProposal } from '../../../hooks/useSopProposals';

const FEEDBACK_MIN_LENGTH = 10;
const FEEDBACK_MAX_LENGTH = 2000;

interface ReviseProposalModeProps {
  /** Proposal id being revised. */
  proposalId: string;
  /** Panel visibility (parent state — toggled by "Ask AI to revise" button). */
  isOpen: boolean;
  /** Called when trainer cancels or closes the panel. */
  onClose: () => void;
  /**
   * Called on a successful revise (NOT on the `unchanged` case). The parent
   * typically closes the panel and lets the detail query refetch to re-render
   * the revised proposal.
   */
  onRevised: () => void;
  /**
   * Current reviseCount on the proposal. Displayed as "Revision #N+1" so the
   * trainer knows which cycle they're about to initiate.
   */
  reviseCount: number;
}

export default function ReviseProposalMode({
  proposalId,
  isOpen,
  onClose,
  onRevised,
  reviseCount,
}: ReviseProposalModeProps) {
  const [feedback, setFeedback] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [unchangedMessage, setUnchangedMessage] = useState<string | null>(null);

  const reviseMutation = useReviseProposal();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset local state each time the panel opens so stale text / errors /
  // "unchanged" banners from a prior cycle don't leak into the new session.
  useEffect(() => {
    if (isOpen) {
      setFeedback('');
      setValidationError(null);
      setServerError(null);
      setUnchangedMessage(null);
      // Focus the textarea on open so the trainer can start typing immediately.
      // Short delay so the expansion animation settles first.
      const t = window.setTimeout(() => textareaRef.current?.focus(), 120);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  const handleSubmit = useCallback(() => {
    // Clear any prior unchanged/error banners so the next outcome is clearly
    // attributed to THIS submission.
    setValidationError(null);
    setServerError(null);
    setUnchangedMessage(null);

    const trimmed = feedback.trim();
    if (trimmed.length < FEEDBACK_MIN_LENGTH) {
      setValidationError(
        `Please describe what should change in at least ${FEEDBACK_MIN_LENGTH} characters.`
      );
      textareaRef.current?.focus();
      return;
    }
    if (trimmed.length > FEEDBACK_MAX_LENGTH) {
      setValidationError(
        `Feedback is too long (${trimmed.length}/${FEEDBACK_MAX_LENGTH} characters).`
      );
      return;
    }

    reviseMutation.mutate(
      { proposalId, feedback: trimmed },
      {
        onSuccess: (result) => {
          if (result.unchanged) {
            // Claude produced identical output — keep the panel open so the
            // trainer can refine their feedback. Message comes from server.
            setUnchangedMessage(
              result.message ??
                "AI didn't change anything — try more specific feedback."
            );
            return;
          }
          // Happy path: close the panel and let the parent refetch the
          // proposal detail query (via onRevised → TanStack invalidation).
          onRevised();
        },
        onError: (err) => {
          // Surface server-side errors (502 claude failure, 400 validation,
          // 403/404/409) in an error banner. Trainer can retry without losing
          // what they typed.
          const msg =
            err instanceof Error
              ? err.message
              : 'Revise failed. Please try again.';
          setServerError(msg);
        },
      }
    );
  }, [feedback, proposalId, reviseMutation, onRevised]);

  if (!isOpen) return null;

  const trimmedLength = feedback.trim().length;
  const belowMin = trimmedLength < FEEDBACK_MIN_LENGTH;
  const isSubmitting = reviseMutation.isPending;
  const canSubmit = !isSubmitting && !belowMin;

  return (
    <section
      role="region"
      aria-label="Revise proposal"
      className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5 shadow-sm"
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <MessageSquarePlus
            size={18}
            strokeWidth={2.25}
            className="mt-0.5 flex-shrink-0 text-indigo-700"
          />
          <div>
            <h3 className="text-sm font-bold text-indigo-900">
              Ask the AI to revise
            </h3>
            <p className="mt-0.5 text-xs text-indigo-800/80">
              Tell the AI what to change. It rewrites the proposal; you review
              the result and decide whether to approve.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 ring-1 ring-indigo-200">
            Revision #{reviseCount + 1}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close revise panel"
            className="rounded-md p-1 text-indigo-700 hover:bg-white/60 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Textarea */}
      <div>
        <label
          htmlFor="revise-feedback"
          className="sr-only"
        >
          What should the AI change?
        </label>
        <textarea
          id="revise-feedback"
          ref={textareaRef}
          value={feedback}
          onChange={(e) => {
            setFeedback(e.target.value);
            // Clear stale banners as the trainer types so they're not
            // distracted by out-of-date feedback.
            if (unchangedMessage) setUnchangedMessage(null);
            if (validationError) setValidationError(null);
            if (serverError) setServerError(null);
          }}
          disabled={isSubmitting}
          placeholder="What should the AI change? Be specific. E.g., 'Step 4.2 is too technical — simplify to a 6th-grade reading level' or 'Add a safety check before step 3.1.'"
          rows={5}
          maxLength={FEEDBACK_MAX_LENGTH + 100 /* hard stop slightly past max so we can surface a friendly error */}
          className="block w-full resize-y rounded-xl border border-indigo-200 bg-white p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <div className="mt-1.5 flex items-center justify-between text-xs">
          <span className="text-indigo-900/60">
            {belowMin
              ? `At least ${FEEDBACK_MIN_LENGTH} characters`
              : 'Ready to submit'}
          </span>
          <span
            className={
              trimmedLength > FEEDBACK_MAX_LENGTH
                ? 'font-semibold text-rose-700'
                : 'text-indigo-900/60'
            }
          >
            {trimmedLength}/{FEEDBACK_MAX_LENGTH}
          </span>
        </div>
      </div>

      {/* Validation error (client-side) */}
      {validationError && (
        <p
          role="alert"
          aria-live="polite"
          className="mt-3 flex items-start gap-1.5 rounded-md bg-rose-50 p-2 text-xs text-rose-800 ring-1 ring-rose-200"
        >
          <AlertCircle
            size={12}
            className="mt-0.5 flex-shrink-0"
            strokeWidth={2.5}
          />
          <span>{validationError}</span>
        </p>
      )}

      {/* "Unchanged" info banner */}
      {unchangedMessage && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-amber-200"
        >
          <Info
            size={12}
            className="mt-0.5 flex-shrink-0"
            strokeWidth={2.5}
          />
          <span>{unchangedMessage}</span>
        </p>
      )}

      {/* Server error */}
      {serverError && (
        <p
          role="alert"
          aria-live="polite"
          className="mt-3 flex items-start gap-1.5 rounded-md bg-rose-50 p-2 text-xs text-rose-800 ring-1 ring-rose-200"
        >
          <AlertCircle
            size={12}
            className="mt-0.5 flex-shrink-0"
            strokeWidth={2.5}
          />
          <span>{serverError}</span>
        </p>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-busy={isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2
                size={14}
                strokeWidth={2.5}
                className="animate-spin"
                aria-hidden="true"
              />
              <span>AI is revising...</span>
            </>
          ) : (
            <>
              <MessageSquarePlus size={14} strokeWidth={2.5} aria-hidden="true" />
              <span>Ask AI to revise</span>
            </>
          )}
        </button>
      </div>

      {/* Subtle latency hint — helps set expectations on the 3-10s Claude call. */}
      {isSubmitting && (
        <p
          aria-live="polite"
          className="mt-2 text-right text-[11px] text-indigo-700/80"
        >
          Claude typically takes 3–10 seconds to revise.
        </p>
      )}
    </section>
  );
}
